// Мастер «Пригласить выбранных»: раздать вход пачкой, одной ролью на всех.
//
// ## Ради чего это существует
//
// Заказчик 14.09.2026: «надо, чтоб сразу адаптированно разослать ссылки людям, с кем работает
// заказчик, и чтобы все быстро влились». По одному это выглядело так: открыть настройки, завести
// запись, вернуться, назначить роль, нажать «Дать код», переписать код — и так двадцать раз, а
// у него 281 охранник. Раздача кодов пачкой в настройках была, но она умеет только уже
// заведённых и отдаёт КОД, а не ссылку: восемь знаков, которые человеку в перчатке надо
// продиктовать и попросить набрать.
//
// Здесь пачка идёт от РЕЕСТРА ЛЮДЕЙ, то есть от тех, кто в системе ещё не заведён, и отдаёт
// готовый список «фамилия и ссылка», который копируется целиком одним нажатием.
//
// ## Почему по одному, а не одной дверью «выдать всем»
//
// Дверь на коды пачкой у сервера есть, но она отвечает на другой вопрос: «выдай код вот этим
// учётным записям». Здесь учётных записей у половины людей ещё нет, каждому надо подобрать
// свободное имя входа и поставить роль — то есть шаги всё равно свои на каждого. Зато при таком
// ходе один сорвавшийся человек не роняет раздачу остальным: у него в списке появляется причина,
// а остальные получают свои ссылки.
//
// ## Панель, а не полоса в подвале списка
//
// Результат — два десятка строк со ссылками, и в узкой колонке реестра он не помещается.
// Выделение приезжает памятью, как в мастер укомплектования: двадцать опознавателей в адресной
// строке — это ссылка, которую нельзя ни прочитать, ни переслать.

import { useState } from 'react';

import { RiErrorWarningLine, RiLinkM } from '@remixicon/react';

import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { CheckboxField } from '@/shared/ui/checkbox-field';
import { КнопкаКопирования } from '@/shared/ui/copy-button';
import * as Hint from '@/shared/ui/hint';
import { Skeleton } from '@/shared/ui/screen-state';
import { useРоли, useУчётныеЗаписи, учётнаяЗаписьЧеловека, ключиУчётных } from '@/shared/users/queries';
import { useQueryClient } from '@tanstack/react-query';

import { выдатьДоступ, списокСсылок, type ИтогВыдачи } from '../access/выдача';
import { PERSON_FORMS } from '../dictionaries';
import { взятьПриглашаемых } from './staff-selection';

const ПРАВО_МЕНЯТЬ = 'core.user.manage';
const ПРАВО_КОДОВ = 'core.user.invite';
const ПРАВО_РОЛЕЙ = 'core.role.assign';

export function InvitePanel({ onClose }: { onClose: () => void }) {
  const права = useAuthStore((состояние) => состояние.permissions);
  const можноМенять = can(права, ПРАВО_МЕНЯТЬ);
  const можноКоды = can(права, ПРАВО_КОДОВ);
  const можноРоли = can(права, ПРАВО_РОЛЕЙ);

  const клиент = useQueryClient();
  // Выделение забирается ОДИН раз, при открытии панели: дальше оно живёт здесь, а ячейка
  // памяти свободна для следующего мастера.
  const [строки] = useState(() => взятьПриглашаемых());
  const [роли, setРоли] = useState<string[]>([]);
  const [вПрограмму, setВПрограмму] = useState(false);
  const [подсказка, setПодсказка] = useState<string | null>(null);
  const [сделано, setСделано] = useState(0);
  const [идёт, setИдёт] = useState(false);
  const [итоги, setИтоги] = useState<ИтогВыдачи[] | null>(null);

  const учётные = useУчётныеЗаписи();
  const справочникРолей = useРоли();

  const доступныеРоли = (справочникРолей.data ?? []).filter((роль) => роль.assignable);

  async function раздать() {
    if (идёт) {
      setПодсказка('Уже раздаём, ждём сервер. Второй раз не отправляем.');
      return;
    }
    if (!можноКоды) {
      setПодсказка(
        'Ссылки для входа выдают администратор, финансовый директор, руководитель и операционный руководитель. Попросите кого-то из них.',
      );
      return;
    }
    if (можноРоли && роли.length === 0) {
      setПодсказка(
        'Сначала выберите роль: без неё люди войдут и не увидят ни своего расчётного листа, ни денег.',
      );
      return;
    }
    setПодсказка(null);
    setИдёт(true);
    setСделано(0);
    const собрано: ИтогВыдачи[] = [];
    // Последовательно, а не пачкой запросов разом. Счётчик «7 из 12» растёт на каждом
    // человеке — при двадцати людях это единственное, что отличает работу от зависания.
    for (const человек of строки) {
      const итог = await выдатьДоступ(
        {
          employeeId: человек.id,
          фамилия: человек.фамилия,
          запись: учётнаяЗаписьЧеловека(учётные.data, человек.id) ?? null,
        },
        {
          роли,
          вПрограмму,
          можноРоли,
          можноЗаводить: можноМенять,
          менятьРольСуществующему: false,
        },
      );
      собрано.push(итог);
      setСделано(собрано.length);
    }
    setИтоги(собрано);
    setИдёт(false);
    void клиент.invalidateQueries({ queryKey: ключиУчётных.все });
  }

  if (строки.length === 0) {
    return (
      <div className='flex flex-col gap-3'>
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Люди не выбраны. Так бывает после обновления страницы: выделение живёт на экране, а
            не в адресе. Отметьте людей в реестре галочками и нажмите «Пригласить выбранных» ещё
            раз.
          </span>
        </Alert.Root>
        <div>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={onClose}
          >
            Закрыть
          </Button.Root>
        </div>
      </div>
    );
  }

  if (итоги !== null) {
    return <ГотовыеСсылки итоги={итоги} закрыть={onClose} />;
  }

  return (
    <div className='flex flex-col gap-3'>
      <p className='text-paragraph-sm text-text-sub-600'>
        Каждый получит свою ссылку для входа в приложение. Кого ещё нет в системе — заведём тут
        же, имя входа подберём сами. Ссылка годна трое суток.
      </p>

      <div className='flex flex-col gap-1 rounded-lg bg-bg-weak-50 px-3 py-2'>
        <span className='text-label-sm text-text-strong-950'>
          {countLabel(строки.length, PERSON_FORMS)}
        </span>
        <ul className='flex flex-col'>
          {строки.map((человек) => {
            const запись = учётнаяЗаписьЧеловека(учётные.data, человек.id);
            return (
              <li
                key={человек.id}
                className='flex flex-wrap items-baseline justify-between gap-2 py-0.5'
              >
                <span className='text-paragraph-sm text-text-strong-950'>{человек.фамилия}</span>
                {/* «Смотрим…» отличается от «в системе ещё нет» намеренно: пока список
                    учётных записей едет, ответа мы не знаем, а написанное «нет» человек
                    читает как ответ. */}
                <span className='text-paragraph-xs text-text-sub-600'>
                  {запись === undefined
                    ? 'смотрим…'
                    : запись === null
                      ? 'в системе ещё нет'
                      : `вход «${запись.login}»`}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Сначала РОЛЬ, потом ссылки. Ссылка, выданная до роли, пускает человека туда, где ему
          не видно ничего, и он читает это как поломку программы. */}
      {можноРоли ? (
        <div className='flex flex-col gap-2'>
          <span className='text-paragraph-sm text-text-sub-600'>
            Роль одна на всех выбранных: на посту работа одинаковая. Кому нужна другая, потом в
            карточке этого человека, вкладка «Доступ».
          </span>
          {справочникРолей.isPending ? (
            <Skeleton className='h-16 w-full' />
          ) : (
            <div className='grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2'>
              {доступныеРоли.map((роль) => (
                <CheckboxField
                  key={роль.code}
                  id={`пачка-роль-${роль.code}`}
                  label={роль.title}
                  checked={роли.includes(роль.code)}
                  onCheckedChange={() =>
                    setРоли((было) =>
                      было.includes(роль.code)
                        ? было.filter((код) => код !== роль.code)
                        : [...было, роль.code],
                    )
                  }
                />
              ))}
            </div>
          )}
          {!справочникРолей.isPending &&
            доступныеРоли.length < (справочникРолей.data ?? []).length && (
              <span className='text-paragraph-xs text-text-sub-600'>
                Роли старше вашей здесь не перечислены: выдать их может финансовый или
                генеральный директор.
              </span>
            )}
        </div>
      ) : (
        <Hint.Root>
          Роль назначает администратор или финансовый директор. Вы можете выдать ссылки тем, у
          кого роль уже есть.
        </Hint.Root>
      )}

      {можноМенять && (
        <CheckboxField
          id='пачка-в-программу'
          label='Смогут входить в программу на компьютере'
          checked={вПрограмму}
          onCheckedChange={() => setВПрограмму((было) => !было)}
        />
      )}

      <div className='flex flex-wrap items-center gap-2'>
        <Button.Root
          type='button'
          variant='primary'
          mode='filled'
          size='xsmall'
          onClick={() => void раздать()}
        >
          <Button.Icon as={RiLinkM} />
          {идёт
            ? `Выдаём… ${сделано} из ${строки.length}`
            : `Выдать ссылки (${строки.length})`}
        </Button.Root>
        <Button.Root
          type='button'
          variant='neutral'
          mode='ghost'
          size='xxsmall'
          onClick={onClose}
        >
          Отмена
        </Button.Root>
      </div>

      {подсказка !== null && <Hint.Root hasError>{подсказка}</Hint.Root>}
    </div>
  );
}

/**
 * Готовый список «фамилия и ссылка».
 *
 * Показывается ОДИН раз: второй раз ссылку не покажет ни программа, ни сервер — в базе лежит
 * отпечаток, а не код. Поэтому кнопка копирования стоит первой, а предупреждение написано до
 * списка, а не после: после списка на двадцать строк его не прочитают.
 */
function ГотовыеСсылки({ итоги, закрыть }: { итоги: ИтогВыдачи[]; закрыть: () => void }) {
  const вышло = итоги.filter((итог) => итог.ссылка !== null);
  const нет = итоги.filter((итог) => итог.ссылка === null);

  return (
    <div className='flex flex-col gap-3'>
      <Alert.Root
        variant='light'
        status={нет.length > 0 ? 'warning' : 'success'}
        size='small'
        role='status'
      >
        <span>
          Ссылки готовы: {вышло.length} из {итоги.length}. Скопируйте список сейчас — второй раз
          показать его нельзя.
        </span>
      </Alert.Root>

      <div className='flex flex-wrap items-center gap-2'>
        <КнопкаКопирования
          что={списокСсылок(итоги)}
          подпись='Скопировать список целиком'
          вид='filled'
        />
        <Button.Root
          type='button'
          variant='neutral'
          mode='ghost'
          size='xxsmall'
          onClick={закрыть}
        >
          Готово
        </Button.Root>
      </div>

      <ul className='flex flex-col divide-y divide-stroke-soft-200'>
        {вышло.map((итог) => (
          <li key={итог.employeeId} className='flex flex-col gap-0.5 py-2'>
            <span className='text-label-sm text-text-strong-950'>{итог.фамилия}</span>
            <span className='break-all text-paragraph-xs text-text-sub-600'>{итог.ссылка}</span>
          </li>
        ))}
      </ul>

      {/* Пропущенные названы поимённо: строка, исчезнувшая из списка, читается как «выдано», и
          человек остаётся без доступа — узнает он об этом на объекте, в смену. */}
      {нет.length > 0 && (
        <div className='flex flex-col gap-1'>
          <span className='text-label-sm text-text-strong-950'>Без ссылки остались</span>
          <ul className='flex flex-col divide-y divide-stroke-soft-200'>
            {нет.map((итог) => (
              <li key={итог.employeeId} className='flex flex-col gap-0.5 py-2'>
                <span className='text-paragraph-sm text-text-strong-950'>{итог.фамилия}</span>
                <span className='text-paragraph-xs text-warning-base'>{итог.причина}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
